// Traduction des échecs d'envoi et de vérification d'un code à usage unique
// (incident du 2026-08-20, Fnac Darty et Hermès).
//
// Pourquoi un code et pas un lien : les protections de messagerie d'entreprise
// (Microsoft Safe Links, Proofpoint URL Defense) suivent chaque URL des mails
// pour les scanner. Les logs auth montrent le motif à chaque envoi — un
// `/verify` en 303 depuis une IP de datacenter, puis `One-time token not found`
// pour le clic humain. Le jeton à usage unique est donc brûlé avant que
// l'utilisateur ne clique. Un code à 6 chiffres, lui, ne se clique pas.
//
// Restent deux échecs à expliquer proprement, au lieu de laisser passer le
// message brut de GoTrue en anglais :
//   - le cooldown d'envoi (429), 60 s par adresse côté Supabase ;
//   - le code faux ou périmé.

export type OtpErrorCode =
  | 'RATE_LIMITED'
  | 'INVALID_CODE'
  | 'NO_ACCOUNT'
  | 'INVALID_EMAIL'
  | 'UNKNOWN';

export interface OtpErrorInfo {
  code: OtpErrorCode;
  /** Une ligne, affichable telle quelle sous le champ. */
  message: string;
  /** Secondes à attendre avant un nouvel envoi, si le serveur les donne. */
  retryAfterSeconds: number | null;
  /** Message d'origine, gardé pour la console. */
  raw: string;
}

/** Forme minimale d'une AuthError supabase-js — on ne dépend pas du type SDK. */
export interface OtpErrorLike {
  message?: string;
  code?: string;
  status?: number;
}

/**
 * « For security purposes, you can only request this after 47 seconds. »
 * Le délai est dans le message, pas dans un champ dédié.
 */
function extractRetryAfter(message: string): number | null {
  const m = message.match(/after (\d+) seconds?/i);
  return m ? Number(m[1]) : null;
}

export function parseOtpError(error: OtpErrorLike | null | undefined): OtpErrorInfo | null {
  if (!error) return null;

  const raw = error.message ?? '';
  const code = error.code ?? '';
  const lower = raw.toLowerCase();

  // Le cooldown porte sur l'ADRESSE, pas sur le type de mail : une invitation
  // envoyée dans la minute bloque la demande de code qui suit (cas observé le
  // 2026-08-20). Ne pas annoncer « un code vient d'être envoyé », ce serait faux.
  if (code === 'over_email_send_rate_limit' || error.status === 429) {
    const seconds = extractRetryAfter(raw);
    return {
      code: 'RATE_LIMITED',
      message: seconds
        ? `Un email vient d'être envoyé à cette adresse. Patientez ${seconds} s avant d'en demander un autre.`
        : "Un email vient d'être envoyé à cette adresse. Patientez une minute avant d'en demander un autre.",
      retryAfterSeconds: seconds,
      raw,
    };
  }

  // GoTrue renvoie le même code pour un chiffre faux et pour un code périmé :
  // on ne prétend pas distinguer, on dit quoi faire.
  if (
    code === 'otp_expired' ||
    lower.includes('token has expired') ||
    lower.includes('invalid') && lower.includes('token')
  ) {
    return {
      code: 'INVALID_CODE',
      message: 'Code invalide ou expiré. Demandez-en un nouveau.',
      retryAfterSeconds: null,
      raw,
    };
  }

  // shouldCreateUser: false + adresse inconnue. On ne dit PAS « cette adresse
  // n'a pas de compte » à un visiteur non authentifié sur /login — ce serait un
  // annuaire. Sur la page d'invitation l'adresse est déjà connue de l'invitant,
  // le message peut être explicite.
  if (code === 'otp_disabled' || lower.includes('signups not allowed')) {
    return {
      code: 'NO_ACCOUNT',
      message: "Impossible d'envoyer un code à cette adresse. Vérifiez l'orthographe ou contactez la personne qui vous a invité.",
      retryAfterSeconds: null,
      raw,
    };
  }

  if (code === 'validation_failed' || lower.includes('unable to validate email')) {
    return {
      code: 'INVALID_EMAIL',
      message: 'Adresse email invalide.',
      retryAfterSeconds: null,
      raw,
    };
  }

  return {
    code: 'UNKNOWN',
    message: raw || 'Erreur inconnue',
    retryAfterSeconds: null,
    raw,
  };
}
